/* import __COLOCATED_TEMPLATE__ from './empty-state.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import All from 'embercom/objects/inbox/inboxes/all';
import Mentions from 'embercom/objects/inbox/inboxes/mentions';
import Unassigned from 'embercom/objects/inbox/inboxes/unassigned';
import type InboxApi from 'embercom/services/inbox-api';
import type InboxState from 'embercom/services/inbox-state';
import type Session from 'embercom/services/session';
import { use } from 'ember-resources/util/function-resource';
import { AsyncData } from 'embercom/resources/utils/async-data';
import CreatedByYou from 'embercom/objects/inbox/inboxes/created-by-you';
import Team from 'embercom/objects/inbox/inboxes/team';
import View from 'embercom/objects/inbox/inboxes/view';
import Admin from 'embercom/objects/inbox/inboxes/admin';
import type RouterService from '@ember/routing/router-service';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';

interface Args {}

interface Signature {
  Args: Args;
}

export default class Inbox2CommonEmptyState extends Component<Signature> {
  @service declare inboxApi: InboxApi;
  @service declare inboxState: InboxState;
  @service declare session: Session;
  @service declare intercomEventService: any;
  @service declare router: RouterService;
  // Need this service to check if fin is live
  // eslint-disable-next-line @intercom/intercom/service-allow-list
  @service declare aiAgentSetupService: AiAgentSetupService;

  @tracked hasConversations?: boolean;
  @tracked conversationsExistLoaded = false;

  get activeInboxIsMe() {
    return this.inboxState.activeInbox?.id === this.session.teammate.id.toString();
  }

  get activeInboxIsAll() {
    return this.inboxState.activeInbox instanceof All;
  }

  get activeInboxIsMentions() {
    return this.inboxState.activeInbox instanceof Mentions;
  }

  get activeInboxIsUnassigned() {
    return this.inboxState.activeInbox instanceof Unassigned;
  }

  get activeInboxIsCreatedByYou() {
    return this.inboxState.activeInbox instanceof CreatedByYou;
  }

  get activeInboxIsTeam() {
    return this.inboxState.activeInbox instanceof Team;
  }

  get activeInboxIsView() {
    return this.inboxState.activeInbox instanceof View;
  }

  get activeInboxIsTeammate() {
    return this.inboxState.activeInbox instanceof Admin;
  }

  get activeInboxIsAiAgent() {
    let aiAgentInboxes = [
      'ai_agent_all_conversations',
      'ai_agent_resolved',
      'ai_agent_routed',
      'ai_agent_abandoned',
    ];

    return (
      this.inboxState.activeInbox instanceof View &&
      aiAgentInboxes.includes(this.inboxState.activeInbox.viewSummary.systemType)
    );
  }

  get showTestConversationLink() {
    return (
      (this.activeInboxIsMe ||
        this.activeInboxIsMentions ||
        this.activeInboxIsUnassigned ||
        this.activeInboxIsAll) &&
      !this.conversationsExist.value &&
      !this.session.showLightInbox
    );
  }

  get showGenericEmptyChannelMessage() {
    if (this.activeInboxIsAiAgent) {
      return false;
    }

    return (
      !this.session.workspace.isAnyChannelInstalled &&
      (this.activeInboxIsMe ||
        this.activeInboxIsMentions ||
        this.activeInboxIsUnassigned ||
        this.activeInboxIsAll ||
        this.activeInboxIsCreatedByYou ||
        this.activeInboxIsTeam ||
        this.activeInboxIsView ||
        this.activeInboxIsTeammate)
    );
  }

  get isFinChatLive() {
    return (
      this.aiAgentSetupService.isFinSetupChatRulesetLive ||
      this.aiAgentSetupService.isFinLiveInChannelOutsideSetup('chat')
    );
  }

  get isFinEmailLive() {
    return (
      this.aiAgentSetupService.isFinSetupEmailRulesetLive ||
      this.aiAgentSetupService.isFinLiveInChannelOutsideSetup('email')
    );
  }

  get isFinPhoneLive() {
    return (
      this.aiAgentSetupService.isFinSetupPhoneRulesetLive ||
      this.aiAgentSetupService.isFinLiveInChannelOutsideSetup('phone')
    );
  }

  get isFinLive() {
    return this.isFinChatLive || this.isFinEmailLive || this.isFinPhoneLive;
  }

  @use conversationsExist = AsyncData<boolean>(async () => {
    if (this.inboxState.conversationsExist === undefined) {
      this.inboxState.conversationsExist = await this.inboxApi.checkConversationsExist();
    }
    this.conversationsExistLoaded = true;
    return this.inboxState.conversationsExist;
  });

  @action trackAnalyticsFirstImpression() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'inbox_empty_state_hosted_messenger',
      inbox_type: this.inboxState.activeInbox?.type,
    });
  }

  @action openChannelsSettings() {
    return this.router.transitionTo('apps.app.settings.channels.all', this.session.workspace.id);
  }

  @action openDeploy() {
    if (this.session.workspace.hasLightStandaloneInbox) {
      this.router.transitionTo('apps.app.standalone.deploy.overview', this.session.workspace.id);
    }
  }

  @action onLearnAboutChannelsClick(event: MouseEvent) {
    event.preventDefault();

    window.Intercom('showArticle', 9955432);

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'inbox_empty_state_learn_about_channels',
      place: 'inbox',
      section: 'inbox_conversations_empty_state',
      inbox_type: this.inboxState.activeInbox?.type,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Common::EmptyState': typeof Inbox2CommonEmptyState;
    'inbox2/common/empty-state': typeof Inbox2CommonEmptyState;
  }
}
